import { PopoverPosition } from '@mui/material/Popover';
import { DeckTransitionProps } from 'components/maps/MapContext';
import { ContextMenuItem } from 'components/maps/MapMarker';
import { KineticMarkerData, MapMarkerContext } from 'components/maps/MapMarkerContext';
import { MjolnirGestureEvent, MjolnirEvent } from 'mjolnir.js';
import { Dispatch, SetStateAction, useContext, useMemo } from 'react';
import { ViewState } from 'react-map-gl/maplibre';
import { useMapTooltipContext } from '../../contexts/MapTooltipProvider';
import { MapEntityClickEvent } from '../../types';
import PickingIconLayer from '../PickingIconLayer';
import { PickedObject } from '../types';
import { useMapClusterEvents } from './useMapClusterEvents';
import { useMapEntityEvents } from './useMapEntityEvents';
import { useMapMarkerIcon } from './useMapMarkerIcon';

type UseMapMarkerLayerProps = {
  markers: Map<string, KineticMarkerData>;
  cluster: boolean;
  showMapMarkers: boolean;
  addToCache: (html: string) => void;
  mapEntityClickEvents?: MapEntityClickEvent[];
  disableClusterSelection?: boolean;
  setViewState: Dispatch<SetStateAction<Partial<ViewState> & DeckTransitionProps>>;
  setAllContextItems: Dispatch<SetStateAction<ContextMenuItem[]>>;
  setContextPosition: Dispatch<SetStateAction<PopoverPosition | undefined>>;
  setContextMenuCoordinates: Dispatch<SetStateAction<number[] | undefined>>;
};

export const useMapMarkerLayer = ({
  markers,
  cluster,
  showMapMarkers,
  addToCache,
  mapEntityClickEvents = [
    MapEntityClickEvent.CONTROL,
    MapEntityClickEvent.LEFT,
    MapEntityClickEvent.RIGHT,
  ],
  disableClusterSelection = false,
  setViewState,
  setAllContextItems,
  setContextPosition,
  setContextMenuCoordinates,
}: UseMapMarkerLayerProps) => {
  const { lastUpdate } = useContext(MapMarkerContext);
  const { onHover } = useMapTooltipContext();
  const lastUpdateTime = lastUpdate.getTime();
  const { onRightClick: onClusterRightClick, onLeftClick: onClusterLeftClick } =
    useMapClusterEvents({ setViewState });

  const { getMarkerIcon } = useMapMarkerIcon();

  const {
    onControlClick: onMapEnitityControlClick,
    onLeftClick: onMapEnitityLeftClick,
    onRightClick: onMapEnitityRightClick,
  } = useMapEntityEvents({
    setAllContextItems,
    setContextPosition,
    setContextMenuCoordinates,
  });

  const mapMarkerLayer = useMemo(
    () =>
      new PickingIconLayer({
        cluster,
        visible: showMapMarkers,
        data: markers,
        pickable: true,
        getPosition: (d) => {
          return d?.properties?.elevation
            ? d?.geometry?.coordinates?.concat(d.properties.elevation)
            : d.geometry.coordinates;
        },

        id: 'icons',
        getSize: (iconData) => {
          return getMarkerIcon(iconData, addToCache).height;
        },
        billboard: true,
        autoHighlight: true,
        onClick(
          pickingInfo: PickedObject<{
            clusters: { ids: string }[];
            properties: { cluster: { ids: string[] } };
          }>,
          e: MjolnirEvent
        ) {
          const event = e as MjolnirGestureEvent;
          if (pickingInfo.object?.properties.cluster) {
            !disableClusterSelection && onClusterRightClick(pickingInfo, event);
            return onClusterLeftClick(pickingInfo, event);
          }
          if (event.rightButton) {
            mapEntityClickEvents.includes(MapEntityClickEvent.RIGHT) &&
              onMapEnitityRightClick(pickingInfo, event);
            return;
          }
          if (event.srcEvent.ctrlKey) {
            mapEntityClickEvents.includes(MapEntityClickEvent.CONTROL) &&
              onMapEnitityControlClick(pickingInfo, event);
            return;
          }
          mapEntityClickEvents.includes(MapEntityClickEvent.LEFT) &&
            onMapEnitityLeftClick(pickingInfo, event);
        },
        onHover,
        getIcon: (iconData) => {
          return getMarkerIcon(iconData, addToCache);
        },
        updateTriggers: {
          getIcon: lastUpdateTime,
        },
        order: 3,
      }),
    [
      cluster,
      showMapMarkers,
      markers,
      onHover,
      lastUpdateTime,
      addToCache,
      mapEntityClickEvents,
      onMapEnitityLeftClick,
      disableClusterSelection,
      onClusterRightClick,
      onClusterLeftClick,
      onMapEnitityRightClick,
      onMapEnitityControlClick,
      getMarkerIcon,
    ]
  );

  return { mapMarkerLayer };
};
